import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Bot, Search, FolderOpen, GitMerge, Users, FileBarChart, 
  Tags, Shield, Database, Clock, Star, Zap, TrendingUp,
  BarChart3, ArrowRight
} from 'lucide-react';

const showcases = [
  {
    id: 'core',
    label: 'CORE',
    headline: 'Turn Project Chaos into Clarity with Ufficient Core',
    subhead: 'From Blueprints to Briefs',
    body: "Ufficient's Core Module centralizes documents, tasks, and workflows giving construction, engineering, and legal teams a secure, intelligent platform to deliver faster.",
    accentText: 'text-blue-600',
    accentBg: 'bg-blue-600',
    gradient: 'from-blue-500 to-indigo-600',
    isDarkRight: true,
    features: [
      { 
        heading: 'AI Assistant for Instant Answers',
        text: 'Surface insights from contracts, reports, and project documents in seconds.',
        icon: Bot 
      },
      { 
        heading: 'Smart Document Search',
        text: 'AI-powered, context-aware search across engineering, legal, and construction files.',
        icon: Search 
      },
      { 
        heading: 'Intelligent Tagging & Auto-Categorization',
        text: 'Organize proposals, contracts, and compliance docs automatically.',
        icon: Tags 
      },
      { 
        heading: 'Version Control & Audit-Ready Tracking',
        text: 'Always access the latest file with compliance history intact.',
        icon: FolderOpen 
      }
    ]
  },
  {
    id: 'bidai',
    label: 'BIDAI',
    headline: 'Win More Projects with AI‑Powered Bid Management',
    subhead: 'From Smarter Bids to Faster Decisions',
    body: 'BidAI streamlines bid preparation and analysis for construction, engineering, and infrastructure teams helping you reduce errors, save time, and secure contracts faster.',
    accentText: 'text-emerald-600',
    accentBg: 'bg-emerald-600',
    gradient: 'from-emerald-500 to-teal-600',
    isDarkRight: true,
    features: [
      { 
        heading: 'Automated Bid Preparation',
        text: 'Tailored for construction, infrastructure, and engineering projects.',
        icon: Bot 
      },
      { 
        heading: 'AI-Powered Risk & Cost Analysis',
        text: 'Instantly identify risks, pricing gaps, and winning opportunities.',
        icon: TrendingUp 
      },
      { 
        heading: 'Centralized Bid Data',
        text: 'Version control with secure, team-wide collaboration on proposals.',
        icon: Database 
      },
      { 
        heading: 'Faster RFP Turnaround',
        text: 'Submit polished, competitive bids in record time.',
        icon: Clock 
      }
    ]
  },
  {
    id: 'mergeai',
    label: 'MERGEAI',
    headline: 'Transform Document Chaos into Ready‑to‑Use Templates',
    subhead: 'From Manual Copy‑Paste to Automated Excellence',
    body: 'MergeAI automates repetitive document preparation by turning scattered files into ready‑to‑use templates. Instead of wasting hours copying and pasting across contracts, compliance forms, or RFP submissions, MergeAI pulls content directly from your source documents and fills it into structured templates. It saves time, reduces human error, and ensures consistency across all critical documentation.',
    accentText: 'text-violet-600',
    accentBg: 'bg-violet-600',
    gradient: 'from-violet-500 to-purple-600',
    isDarkRight: true,
    features: [
      { 
        heading: 'Automated Template Generation',
        text: 'Build reusable, exportable templates for contracts, compliance forms, RFPs, and reports.',
        icon: FileBarChart 
      },
      { 
        heading: 'Smart Content Population',
        text: 'Automatically insert the right clauses, data, and sections from source documents into templates.',
        icon: Bot 
      },
      { 
        heading: 'Consistency at Scale',
        text: 'Standardize document creation across teams and projects, eliminating copy‑paste errors.',
        icon: Shield 
      },
      { 
        heading: 'Time & Cost Savings',
        text: 'Free high‑cost professionals from repetitive admin work for higher‑value tasks.',
        icon: Clock 
      }
    ]
  },
  {
    id: 'recruitai',
    label: 'RECRUITAI',
    headline: 'Find the Right Talent, Faster with RecruitAI',
    subhead: 'From Smarter Screening to Stronger Teams',
    body: 'RecruitAI streamlines recruitment for construction, engineering, and legal teams by automating candidate screening, ranking, and shortlisting. It reduces manual workload and ensures the right talent is identified quickly.',
    accentText: 'text-cyan-600',
    accentBg: 'bg-cyan-600',
    gradient: 'from-cyan-500 to-blue-600',
    isDarkRight: true,
    features: [
      { 
        heading: 'AI-Powered Candidate Screening',
        text: 'Tailored for technical, engineering, and legal industry roles.',
        icon: Users 
      },
      { 
        heading: 'Smart Ranking & Shortlisting',
        text: 'Filter candidates by skills, compliance requirements, and cultural fit.',
        icon: Star 
      },
      { 
        heading: 'Centralized Candidate Data',
        text: 'Secure collaboration with version control and audit-ready records.',
        icon: Database 
      },
      { 
        heading: 'Faster Hiring Cycles',
        text: 'Automate shortlists, scheduling, and notifications to cut time-to-hire.',
        icon: Clock 
      }
    ]
  },
  {
    id: 'reportbuilder',
    label: 'REPORT BUILDER',
    headline: 'Create Customized, Compliance‑Ready Reports with Report Builder',
    subhead: 'From Raw Data to Reliable Insights',
    body: 'Report Builder transforms scattered project data into professional, compliance‑ready reports tailored for construction, engineering, and legal teams. Spend less time compiling documents and more time making informed decisions.',
    accentText: 'text-amber-600',
    accentBg: 'bg-amber-600',
    gradient: 'from-amber-500 to-orange-600',
    isDarkRight: true,
    features: [
      { 
        heading: 'AI‑Powered Report Generation',
        text: 'Create reports tailored for construction, engineering, and legal workflows.',
        icon: FileBarChart 
      },
      { 
        heading: 'Compliance‑Ready Templates',
        text: 'Meet industry and regulatory standards with pre‑built frameworks.',
        icon: Shield 
      },
      { 
        heading: 'Real‑Time Monitoring & Visualization',
        text: 'Track performance with automated dashboards and insights.',
        icon: BarChart3 
      },
      { 
        heading: 'Centralized Reporting Hub',
        text: 'Secure collaboration with version control and audit‑ready history.',
        icon: Database 
      }
    ]
  }
];

const Card = ({ data, index }) => {
  const isEven = index % 2 === 0;
  
  return (
    <div className="w-full min-h-[500px] md:h-[600px] max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr,1fr] rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-2xl min-h-full">
        {/* Left side - always bigger, alternates between dark and light */}
        <div className={`p-3 sm:p-8 md:p-12 lg:p-16 min-h-[280px] md:min-h-full flex flex-col justify-center ${isEven ? 'bg-slate-900 text-white' : 'bg-slate-50'}`}>
          <div className="mb-4">
            <span className={`text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] ${isEven ? 'text-blue-400' : 'text-blue-600'}`}>{data.label}</span>
          </div>
          <h3 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-4 ${isEven ? 'text-white' : 'text-slate-900'}`}>
            {data.headline}
          </h3>
          <h4 className={`text-base sm:text-lg md:text-xl font-semibold mb-3 ${isEven ? 'text-slate-300' : 'text-slate-700'}`}>{data.subhead}</h4>
          
          {/* Desktop version only - full text */}
          <p className={`hidden sm:block text-base md:text-lg leading-relaxed mb-6 md:mb-8 ${isEven ? 'text-slate-300' : 'text-slate-600'}`}>{data.body}</p>
          <div>
            <Link to="/contact">
              <button className="inline-flex items-center px-6 py-4 rounded-xl text-white font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-lg transition-all duration-300">
                Explore
                <ArrowRight size={18} className="ml-2" />
              </button>
            </Link>
          </div>
        </div>
        
        {/* Right side - always smaller, alternates between light and dark */}
        <div className={`p-3 sm:p-8 md:p-12 lg:p-14 min-h-[200px] md:min-h-full ${isEven ? 'bg-slate-50' : 'bg-slate-900'}`}>
          <div className="space-y-4 md:space-y-5">
            {data.features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div key={idx} className="flex items-start space-x-3 md:space-x-4">
                  <div className={`flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center ${isEven ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-blue-900 text-blue-300 border border-blue-800'}`}>
                    <Icon size={18} className="md:w-5 md:h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className={`font-bold text-sm md:text-base leading-snug mb-1 ${isEven ? 'text-slate-900' : 'text-white'}`}>{f.heading}</h5>
                    <p className={`text-xs md:text-sm leading-relaxed ${isEven ? 'text-slate-600' : 'text-slate-300'}`}>{f.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ShowcaseWheel = () => {
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Compute scroll relative to the container
      const containerTop = container.getBoundingClientRect().top + window.scrollY;
      const rawScrollTop = window.scrollY - containerTop;
      const maxScroll = container.scrollHeight - window.innerHeight;
      const clamped = Math.max(0, Math.min(maxScroll, rawScrollTop));

      // Determine active card based on the viewport center crossing each segment
      const perCard = maxScroll / showcases.length;
      const middlePosition = clamped + window.innerHeight / 2;
      const index = Math.min(
        showcases.length - 1,
        Math.floor(middlePosition / perCard)
      );

      setCurrentIndex(index);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // initialize
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-0 text-center">
        <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900">One Platform. Zero Friction.</h2>
      </div>

      {/* Scroll container that creates height */}
      <div 
        ref={containerRef} 
        className="relative -mt-20 md:-mt-28 lg:-mt-32"
        style={{ height: `${showcases.length * 120}vh` }}
      >
        {/* Fixed card display */}
        <div className="sticky top-0">
          <div className="h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 -translate-y-[40vh] md:-translate-y-[30vh]">
            <div className="relative w-full">
              {showcases.map((data, index) => (
                <motion.div
                  key={data.id}
                  className="absolute w-full"
                  initial={false}
                  animate={{
                    opacity: index === currentIndex ? 1 : 0,
                    scale: index === currentIndex ? 1 : 0.95,
                    rotateX: index < currentIndex ? -10 : index > currentIndex ? 10 : 0,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut"
                  }}
                  style={{
                    pointerEvents: index === currentIndex ? 'auto' : 'none',
                    transformStyle: 'preserve-3d',
                    perspective: 1000
                  }}
                >
                  <Card data={data} index={index} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseWheel; 